import { useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import {
  CodingStrategyEnum,
  getDocumentName,
  MissionKindEnum,
} from '@e-socna/data-layer';
import { EMPTY_ID } from '@e-socna/empty-client';
import { ensureDefined } from '@e-socna/utils';
import type { CodingConfigUnionInput, Id } from '@e-socna/data-layer';

import { userFriendlyErrors } from '../../../utils/back-errors.util';
import { PATH_MISSION_GENERAL } from '../../../app/constants/paths.constant';
import { useViewSnackbarAction } from '../../../hooks/useViewSnackbarAction.hook';
import { useGraphQLMutation } from '../../../hooks/useGraphQLMutation.hook';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { MissionPage_OneMission_QueryDocument } from '../../../pages/Mission/hooks/useFindOneMissionQuery.hook';
import { FolderPage_OneFolder_QueryDocument } from '../../../pages/Folder/hooks/useFindOneFolderQuery.hook';
import type { BackError } from '../../../utils/back-errors.util';
import type { WizardSubmitHandlerParams } from '../../../components/Wizard/types/submit-handler.type';

import { memoForm2ApiMapper } from '../../MemoBeforeSubmit/mappers/memo.form2api.mapper';
import { FormVariant } from '../../enums/variant.enum';

import { missionForm2ApiMapper } from '../mappers/mission.form2api.mapper';
import { collaborationTurnForm2ApiMapper } from '../mappers/collaboration-turn.form2api.mapper';
import { isTurningIntoCollaboration } from '../utils/collaboration-turn.util';
import { creationRedirectPathOf } from '../utils/creation-redirect-path.util';
import { filterMissionWorkflowsOfKind } from '../utils/mission-workflows-of-kind.util';
import {
  filterMissionWorkflowsOfFamily,
  findMissionWorkflowFamily,
} from '../utils/mission-workflow-family.util';
import type { MissionFormTab } from '../enums/tab.enum';
import type { MissionForm_Mission } from '../fragments/mission.fragment';

import { useInitMissionMutation } from './useInitMissionMutation.hook';
import { useUpdateMissionMutation } from './useUpdateMissionMutation.hook';
import { MissionForm_CodingConfigStep_CodeSuggestion_QueryDocument } from './useCodeSuggestion.hook';
import { useTurnMissionIntoCollaborationMutation } from './useTurnMissionIntoCollaborationMutation.hook';
import type { MissionForm_Mission_Init_Result } from './useInitMissionMutation.hook';
import type { MissionForm_Mission_Update_Result } from './useUpdateMissionMutation.hook';
import type { SubMissionTurnLists } from './useSubMissionTurnLists.hook';
import { buildMissionPath } from '../../../app/utils/mission-space.util';

export type MissionMutationResult =
  MissionForm_Mission_Init_Result | MissionForm_Mission_Update_Result;

interface Params {
  readonly formVariant?: FormVariant;
  readonly handleSuccess?: (result: MissionMutationResult) => void;
  readonly missionId?: Id;
  readonly subMissionTurnLists?: SubMissionTurnLists;
}

export const useMissionSubmit = ({
  formVariant,
  handleSuccess,
  missionId,
  subMissionTurnLists,
}: Readonly<Params>) => {
  const { enqueueSnackbar } = useSnackbar();
  const { buildViewSnackbarAction } = useViewSnackbarAction();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { mutate: initMutation } = useInitMissionMutation();
  const { mutate: updateMutation } = useUpdateMissionMutation();
  const { mutateAsync: turnIntoCollaborationMutation } =
    useTurnMissionIntoCollaborationMutation();
  const { mutateAsync: suggestCodeMutation } = useGraphQLMutation(
    MissionForm_CodingConfigStep_CodeSuggestion_QueryDocument,
  );

  const { codingStrategiesCodeMap, missionWorkflows, workflowFamilies } =
    useReferencesContext();

  const handleSubmit = useCallback(
    ({
      values,
      memos,
      formikHelpers: { setSubmitting },
      setWizardError,
    }: Readonly<
      WizardSubmitHandlerParams<MissionForm_Mission, MissionFormTab>
    >) => {
      setWizardError(undefined);

      const path = creationRedirectPathOf(values);

      const onSuccess = (result: MissionMutationResult) => {
        if (formVariant === FormVariant.Creation) {
          const { _id } = (result as MissionForm_Mission_Init_Result)
            .initMission;

          enqueueSnackbar('Votre nouvelle mission a bien été créée.', {
            action: buildViewSnackbarAction(
              buildMissionPath(PATH_MISSION_GENERAL, {
                folderId: values.folderId,
                missionId: _id,
                kind: values.kind,
                compositeMissionId: values.compositeMissionId,
              }),
            ),
          });
        } else {
          enqueueSnackbar('Votre mission a bien été mise à jour.');
        }

        // The mission, and the collaborations counting their sub-missions (menu, pricing): a
        // sub-mission created, attached or detached changes the former and the new one.
        void queryClient.invalidateQueries({
          queryKey: [getDocumentName(MissionPage_OneMission_QueryDocument)],
        });

        void queryClient.invalidateQueries({
          queryKey: [
            getDocumentName(FolderPage_OneFolder_QueryDocument),
            { id: values.folderId },
          ],
        });

        if (handleSuccess) {
          handleSuccess(result);
        } else if (!formVariant || formVariant === FormVariant.Creation) {
          void navigate(path);
        }

        setSubmitting(false);
      };

      const onError = (error: unknown) => {
        setWizardError({
          messages: userFriendlyErrors(error as BackError),
        });

        setSubmitting(false);
      };

      if (isTurningIntoCollaboration(values)) {
        // The collaboration takes the workflow of the family of the mission.
        const collaborationWorkflowIds = ensureDefined({
          collaborationWorkflowId: filterMissionWorkflowsOfKind(
            filterMissionWorkflowsOfFamily(
              missionWorkflows,
              findMissionWorkflowFamily(workflowFamilies, values.workflowId),
            ),
            MissionKindEnum.Composite,
          )[0]?._id,
        });
        const turnArgs = (codingConfig: CodingConfigUnionInput) =>
          collaborationTurnForm2ApiMapper(
            values,
            {
              ...collaborationWorkflowIds,
              codingConfig,
            },
            subMissionTurnLists,
          );

        // A mission is never coded automatically by the back: the code of the
        // collaboration is suggested first, then set as a manual code, as the
        // creation forms do.
        void suggestCodeMutation({
          mission: turnArgs({
            automatic: {
              kind: CodingStrategyEnum.CodingConfigAutomatic,
              strategyId: codingStrategiesCodeMap.CodingConfigAutomatic._id,
            },
          }).collaboration,
        })
          .then(({ suggestMissionCode }) =>
            turnIntoCollaborationMutation({
              missionId: missionId ?? EMPTY_ID,
              ...turnArgs({
                manual: {
                  kind: CodingStrategyEnum.CodingConfigManual,
                  strategyId: codingStrategiesCodeMap.CodingConfigManual._id,
                  code: suggestMissionCode,
                },
              }),
              versionData: memoForm2ApiMapper(memos),
            }),
          )
          .then(({ turnMissionIntoCollaboration }) => {
            enqueueSnackbar(
              'Votre mission a bien été transformée en Collaboration.',
            );
            void queryClient.invalidateQueries({
              queryKey: [
                getDocumentName(FolderPage_OneFolder_QueryDocument),
                { id: values.folderId },
              ],
            });
            setSubmitting(false);
            void navigate(
              buildMissionPath(PATH_MISSION_GENERAL, {
                folderId: values.folderId,
                missionId:
                  turnMissionIntoCollaboration.compositeMissionId ?? EMPTY_ID,
                kind: MissionKindEnum.Composite,
              }),
            );
          }, onError);
      } else if (formVariant === FormVariant.Creation) {
        initMutation(
          {
            mission: missionForm2ApiMapper(values),
            versionData: memoForm2ApiMapper(memos),
          },
          {
            onError,
            onSuccess,
          },
        );
      } else {
        updateMutation(
          {
            id: missionId ?? EMPTY_ID,
            update: missionForm2ApiMapper(values),
            versionData: memoForm2ApiMapper(memos),
          },
          {
            onError,
            onSuccess,
          },
        );
      }

      return undefined;
    },
    [
      missionId,
      subMissionTurnLists,
      buildViewSnackbarAction,
      enqueueSnackbar,
      formVariant,
      handleSuccess,
      navigate,
      queryClient,
      initMutation,
      updateMutation,
      turnIntoCollaborationMutation,
      missionWorkflows,
      workflowFamilies,
      codingStrategiesCodeMap.CodingConfigAutomatic._id,
      codingStrategiesCodeMap.CodingConfigManual._id,
      suggestCodeMutation,
    ],
  );

  return { handleSubmit };
};
